import {
  Anchor,
  AspectRatio,
  Center,
  Group,
  Image,
  Paper,
  Skeleton,
  Stack,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import classes from "../../elements/ImageGallery/ImageGallery.module.css";

import carburateLogo from "@/assets/illustrations/carburate-logo-small.svg";
import { DataGrid, LoadedImage } from "@/components/elements";
import { env } from "@/config/env";
import { paths } from "@/config/paths";
import { defaultCurrency } from "@/lib/currency";
import { useMarketCar } from "@/services/market/get-market-car";
import { useMarketCarImages } from "@/services/market/get-market-car-images";
import { Currency } from "@/types/currency";
import { formatCurrency, formatNumber, toPercentFraction } from "@/utils/formatEuro";

import type { LabelValueProps } from "@/components/elements";
import type { FC } from "react";

export type ModelComparePreviewProps = {
  carId: string;
};

function getColorFromValue(value = 0) {
  if (value < 0) {
    return "danger";
  }
  if (value > 0) {
    return "success";
  }
  return undefined;
}

function formatYearOverYear(value?: number) {
  if (value === undefined) return "-";

  return formatNumber(toPercentFraction(value), {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export const ModelComparePreview: FC<ModelComparePreviewProps> = ({ carId }) => {
  const { t } = useTranslation();
  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });
  const { data, isLoading } = useMarketCar({ carId });
  const modelLink = paths.app.markets.car.getHref(carId);

  const { data: gallery, isLoading: galleryIsLoading } = useMarketCarImages({ carId });
  const image = gallery?.results?.[0];

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const intro: LabelValueProps[] = [
    {
      label: t("compare.labels.make"),
      value: data?.make_id ? (
        <Anchor component={Link} to={paths.app.markets.makeDetails.getHref(data.make_id)} size="lg">
          {data.make_name || "-"}
        </Anchor>
      ) : (
        "-"
      ),
    },
    {
      label: t("compare.labels.model"),
      value: data?.model_name || "-",
    },
    ...(data?.variant && data.variant.trim() !== ""
      ? [
          {
            label: t("car.variant"),
            value: data.variant,
          },
        ]
      : []),
    {
      label: t("car.production_years"),
      value: data?.production_years || "-",
    },
    {
      label: t("car.number_of_produced_cars"),
      value: data?.produced_cars ? formatNumber(data.produced_cars) : "-",
    },
  ];

  const values: LabelValueProps[] = [
    {
      label: t("market.table.cols.current_valuation"),
      value: formatCurrency(data?.current_valuation, currency.code),
    },
    {
      label: t("market.table.cols.year_over_year_gain_loss"),
      value: formatYearOverYear(data?.year_over_year_gain_loss),
      c: getColorFromValue(data?.year_over_year_gain_loss),
    },
  ];

  const market: LabelValueProps[] = [
    {
      label: t("compare.labels.lowest_hammer_price_3y"),
      value: formatCurrency(data?.market_prices?.hammer_price_3y?.lowest, currency.code),
    },
    {
      label: t("compare.labels.average_hammer_price_3y"),
      value: formatCurrency(data?.market_prices?.hammer_price_3y?.average, currency.code),
    },
    {
      label: t("compare.labels.highest_hammer_price_3y"),
      value: formatCurrency(data?.market_prices?.hammer_price_3y?.highest, currency.code),
    },
  ];

  const bgColor = isDark ? "#323232" : "#d9d9d9";
  const title = [data?.make_name, data?.model_name].filter(Boolean).join(" - ");

  return (
    <Skeleton visible={isLoading} flex={1}>
      <Stack>
        <Group justify="space-between" wrap="nowrap">
          <Title
            order={2}
            title={title || "-"}
            style={{
              minWidth: 0,
              flex: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Link
              to={modelLink}
              style={{
                textDecoration: "none",
              }}
            >
              {title || "-"}
            </Link>
          </Title>
        </Group>
        <Skeleton visible={galleryIsLoading}>
          <AspectRatio ratio={16 / 9}>
            <Paper h={300} shadow="none">
              {image && image.path.includes(env.API_URL) ? (
                <LoadedImage image={image} />
              ) : image ? (
                <Image
                  src={image.path}
                  alt={image.name}
                  className={classes.mainImage}
                  radius="md"
                  fit="cover"
                />
              ) : (
                <Center bg={bgColor} bdrs="md" h="100%">
                  <Image src={carburateLogo} w="auto" height="50%" alt="Carburate" />
                </Center>
              )}
            </Paper>
          </AspectRatio>
        </Skeleton>
        <DataGrid cols={1} items={intro} gridProps={{ verticalSpacing: "xs" }} horizontal />
        <DataGrid
          cols={1}
          items={values}
          gridProps={{ verticalSpacing: "xs" }}
          horizontal
          title={<Title order={3}>{t("compare.titles.value_costs")}</Title>}
        />
        <DataGrid
          cols={1}
          items={market}
          gridProps={{ verticalSpacing: "xs" }}
          horizontal
          title={<Title order={3}>{t("compare.titles.market_headlines")}</Title>}
        />
      </Stack>
    </Skeleton>
  );
};
